import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import FJCheckBox from '@src/components/FJCheckBox/FJCheckBox';
import CloseIcon from '../../../assets/close.svg?react';
import DeleteIcon from '../../../assets/delete-white.svg?react';
import styles from './FJSelectAll.module.less';
import FJLocalStore from '../../../store/FJLocalStore';
import FJButton, { FJButtonType } from '../FJButton/FJButton';
import { useEscapeExitMultiSelect } from '@src/hooks/useEscapeExitMultiSelect';

export interface FJSelectAllListItem {
    id: string | number;
    status?: string;
}

export interface FJSelectAllProps {
    list: FJSelectAllListItem[];
    selectList: Array<string | number>;
    setSelectList: (ids: Array<string | number>) => void;
    multiDelete: (ids: Array<string | number>) => void;
    isMultipleMode?: boolean;
    clearSelected?: () => void;
    /** 是否计入全选/可选列表；默认排除 processing（详情页） */
    isItemSelectable?: (item: FJSelectAllListItem) => boolean;
    /** @deprecated 保留兼容，当前未使用 */
    appType?: string;
}

function defaultIsSelectableItem(item: FJSelectAllListItem) {
    if (!item.status) {
        return true;
    }
    return item.status !== 'progress' && item.status !== 'processing';
}

export default function FJSelectAll({
    list,
    selectList,
    multiDelete,
    setSelectList,
    isMultipleMode = false,
    clearSelected,
    isItemSelectable = defaultIsSelectableItem,
}: FJSelectAllProps) {
    const isShow = selectList.length > 0 || isMultipleMode;
    const selectNumber = selectList.length;
    const prevIsShowRef = useRef(false);
    const [isExiting, setIsExiting] = useState(false);

    useEffect(() => {
        if (isShow) {
            setIsExiting(false);
            prevIsShowRef.current = true;
            return;
        }
        if (prevIsShowRef.current) {
            setIsExiting(true);
            prevIsShowRef.current = false;
            const timer = window.setTimeout(() => setIsExiting(false), 250);
            return () => window.clearTimeout(timer);
        }
    }, [isShow]);

    const filterList = useMemo(() => list.filter(isItemSelectable), [list, isItemSelectable]);

    const handleClearSelected = useCallback(() => {
        clearSelected?.();
        setSelectList([]);
    }, [clearSelected, setSelectList]);

    useEscapeExitMultiSelect(isShow, handleClearSelected);

    const selectedIdSet = useMemo(() => new Set(selectList.map(String)), [selectList]);
    const isSelectedAll =
        filterList.length > 0 && filterList.every(item => selectedIdSet.has(String(item.id)));

    const handleSelectAllChange = (selected: boolean) => {
        if (selected) {
            setSelectList(filterList.map(item => String(item.id)));
        } else {
            setSelectList([]);
        }
    };

    const selectText = selectNumber > 1 ? FJLocalStore._('projects-selected') : FJLocalStore._('project-selected');

    return (
        <div
            className={classNames(
                styles.select_all_box,
                isShow && styles.show,
                !isShow && isExiting && styles.hide,
            )}
        >
            <FJCheckBox
                dataChange={handleSelectAllChange}
                selected={isSelectedAll}
                value={FJLocalStore._('select-all')}
            />
            <div className={styles.middle}>
                <div className={styles.select_number}>{selectNumber}</div>
                <div className={styles.select_desc}>{selectText}</div>
                <div className={styles.split_line} />
                <FJButton
                    icon={<DeleteIcon />}
                    onClick={() => multiDelete(selectList)}
                    disabled={selectList.length === 0}
                    type={FJButtonType.WARNING}
                    className={styles.delete_btn}
                    textClassName={styles.delete_btn_text}
                    text='delete'
                />
            </div>
            <div className={styles.close_btn_box} onClick={handleClearSelected}>
                <CloseIcon />
            </div>
        </div>
    );
}
